import { useId } from 'react'
import styled from 'styled-components'
import { widgetFont } from '../../widgetTypography'
import useTranslation from '../../../../hooks/useTranslation'
import useAnalyticsFormat from './useAnalyticsFormat'
import { dayKey, periodLabel } from './period'

/*
 * The period, on the subtitle line under the title, with a step to the period
 * before and after it: ‹ This month ›. A native <select> under the styling:
 * keyboard, touch and screen readers get the control the platform gives them,
 * and a phone opens its own picker instead of a menu squeezed into a card.
 *
 * Once stepped away from the present, the select shows the dates instead of
 * the period's name ("September 2026") — an option of its own, hidden from the
 * list, so picking any period from the list brings the card back to now.
 *
 * "Custom range" brings two date fields, drawn apart by `CustomRange` — on the
 * right, under "View all"; either one changing re-reads.
 */

const STEPPED = '__stepped'

const Row = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.1rem 0.4rem;
    min-width: 0;
    ${widgetFont.subtitle}
    color: ${p => p.theme.font.label};
`

const Stepper = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.1rem;
    min-width: 0;
    max-width: 100%;
    /* The first arrow sits on the subtitle's left edge, as the text above does */
    margin-left: ${p => p.$first ? '-0.3rem' : '0'};
`

const Step = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    border: 1px solid transparent;
    border-radius: 0.375rem;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover:not(:disabled) { border-color: ${p => p.theme.table.border}; color: ${p => p.theme.font.color}; }
    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; }
    &:disabled { opacity: 0.3; cursor: default; }
`

const SelectWrap = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    min-width: 0;

    &::after {
        content: '';
        position: absolute;
        right: 0.3rem;
        width: 0.35rem;
        height: 0.35rem;
        border-right: 1.5px solid currentColor;
        border-bottom: 1.5px solid currentColor;
        transform: translateY(-25%) rotate(45deg);
        pointer-events: none;
    }
`

const Select = styled.select`
    appearance: none;
    font: inherit;
    color: ${p => p.$stepped ? p.theme.font.color : 'inherit'};
    font-weight: ${p => p.$stepped ? 600 : 'inherit'};
    background: transparent;
    border: 1px solid transparent;
    border-radius: 0.375rem;
    padding: 0.05rem 1.1rem 0.05rem 0.25rem;
    cursor: pointer;
    max-width: 100%;
    text-overflow: ellipsis;
    /* As wide as the period it shows; still ellipsised when the card is narrower */
    field-sizing: content;

    &:hover { border-color: ${p => p.theme.table.border}; color: ${p => p.theme.font.color}; }
    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; }

    option { color: ${p => p.theme.font.color}; background: ${p => p.theme.dropdown.background}; }
`

const Dates = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.25rem 0.4rem;
    ${widgetFont.subtitle}
    color: ${p => p.theme.font.label};
`

const DateInput = styled.input`
    font: inherit;
    ${widgetFont.caption}
    color: ${p => p.theme.font.color};
    background: ${p => p.theme.form.background};
    border: 1px solid ${p => p.theme.form.border};
    border-radius: 0.375rem;
    padding: 0.1rem 0.3rem;
    color-scheme: ${p => p.theme.mode === 'dark' ? 'dark' : 'light'};

    &:focus-visible { outline: 2px solid ${p => p.theme.font.highlight}; outline-offset: 1px; }
`

const Sep = styled.span`
    opacity: 0.6;
`

const Chevron = ({ back }) => (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path d={back ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const PeriodSelect = ({ period, subtitle }) => {
    const { t, m } = useTranslation()
    const { locale } = useAnalyticsFormat()
    const id = useId()
    const stepped = period.offset !== 0
    const weekText = (week, year) => m('analytics.period.week', [week, year])

    return (
        <Row>
            {subtitle && <><span>{subtitle}</span><Sep aria-hidden="true">·</Sep></>}
            <Stepper $first={!subtitle}>
                <Step type="button" onClick={() => period.step(-1)} aria-label={t('analytics.period.previous')} title={t('analytics.period.previous')}>
                    <Chevron back />
                </Step>
                <SelectWrap>
                    <label htmlFor={id} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                        {t('analytics.period.label')}
                    </label>
                    <Select id={id} value={stepped ? STEPPED : period.period} $stepped={stepped}
                        onChange={e => e.target.value !== STEPPED && period.setPeriod(e.target.value)}>
                        {stepped && <option value={STEPPED} hidden>{periodLabel(period.range, locale, weekText)}</option>}
                        {period.allowed.map(p => <option key={p} value={p}>{t(`analytics.period.${p}`)}</option>)}
                    </Select>
                </SelectWrap>
                <Step type="button" onClick={() => period.step(1)} disabled={!period.canStepForward}
                    aria-label={t('analytics.period.next')} title={t('analytics.period.next')}>
                    <Chevron />
                </Step>
            </Stepper>
        </Row>
    )
}

// The two dates of a custom range — nothing for any other period
export const CustomRange = ({ period }) => {
    const { t } = useTranslation()
    if (period.period !== 'custom') return null
    const from = period.custom?.from ?? dayKey(period.range.from)
    const to = period.custom?.to ?? dayKey(period.range.to)

    return (
        <Dates>
            <DateInput type="date" value={from} max={to} aria-label={t('analytics.period.from')}
                onChange={e => e.target.value && period.setCustom({ from: e.target.value, to })} />
            <Sep aria-hidden="true">–</Sep>
            <DateInput type="date" value={to} min={from} aria-label={t('analytics.period.to')}
                onChange={e => e.target.value && period.setCustom({ from, to: e.target.value })} />
        </Dates>
    )
}

export default PeriodSelect
